/**
 * Review Center API client
 * Powers the /review tabs (custom + weak points).
 */
import apiClient from './apiClient';

export type ItemType = 'vocabulary' | 'knowledgePoint' | 'pattern';

export type PatternCategory =
  | 'tense'
  | 'conditional'
  | 'connector'
  | 'comparison'
  | 'modal'
  | 'passive'
  | 'question'
  | 'relative'
  | 'reported'
  | 'inversion'
  | 'emphasis'
  | 'wish'
  | 'clause'
  | 'phrase';

export type CourseStatus = 'in_progress' | 'completed';

export interface ScopeCourse {
  id: string;
  title: string;
  cefrLevel?: string;
  status: CourseStatus;
  thumbnailUrl?: string;
  tags?: string[];
  seriesTitle?: string;
}

export interface ReviewScope {
  courses: ScopeCourse[];
  patternCategories: PatternCategory[];
}

export interface ReviewItemSummary {
  id: string;
  type: ItemType;
  term: string;
  retrievability?: number;
  dueAt?: string;
  reps?: number;
  lapses?: number;
  courseId?: string;
  cefrLevel?: string;
  isUnpracticed?: boolean;
}

export interface ReviewItemsResponse {
  items: ReviewItemSummary[];
  totalAvailable: number;
}

export type ScopeMode = 'course' | 'cefrGrammar' | 'all';

export interface CustomItemsQuery {
  scopeMode: ScopeMode;
  courseIds?: string[];
  cefrLevels?: string[];
  patternCategories?: PatternCategory[];
  types?: ItemType[];
  retrievabilityMax?: number;
  includeUnpracticed?: boolean;
}

export interface SentenceDrillItem {
  id: string;
  courseId: string;
  courseTitle: string;
  sentenceIndex: number;
  sentenceText: string;
  audioUrl: string | null;
  translation: string | null;
  isErrorSentence: boolean;
  lastShadowingScore: number | null;
  dictationErrorCount: number;
  knowledgePointCount: number;
  grammarPointCount: number;
  vocabularyCount: number;
  wordCount: number;
  /** Authored breath-group splits from speaking_strategy.chunks. The speaking
   *  practice surface uses these to render the headline as
   *  "chunk1 / chunk2 / chunk3" once a take is on screen. Empty when the
   *  segment has no authored speaking strategy. */
  chunks: string[];
}

export interface SentenceDrillResponse {
  items: SentenceDrillItem[];
  totalAvailable: number;
}

const reviewCenterService = {
  async getCustomScope(): Promise<ReviewScope> {
    const { data } = await apiClient.get<ReviewScope>('/review/custom/scope');
    return data;
  },

  async getCustomItems(query: CustomItemsQuery): Promise<ReviewItemsResponse> {
    const params: Record<string, string> = { scopeMode: query.scopeMode };
    if (query.courseIds?.length) params.courseIds = query.courseIds.join(',');
    if (query.cefrLevels?.length) params.cefrLevels = query.cefrLevels.join(',');
    if (query.patternCategories?.length) params.patternCategories = query.patternCategories.join(',');
    if (query.types?.length) params.types = query.types.join(',');
    if (typeof query.retrievabilityMax === 'number') {
      params.retrievabilityMax = query.retrievabilityMax.toString();
    }
    if (query.includeUnpracticed) {
      params.includeUnpracticed = 'true';
    }
    const { data } = await apiClient.get<ReviewItemsResponse>('/review/custom/items', { params });
    return data;
  },

  async getSentenceDrill(
    query: CustomItemsQuery,
    limit?: number,
  ): Promise<SentenceDrillResponse> {
    const params: Record<string, string> = { scopeMode: query.scopeMode };
    if (query.courseIds?.length) params.courseIds = query.courseIds.join(',');
    if (query.cefrLevels?.length) params.cefrLevels = query.cefrLevels.join(',');
    if (typeof limit === 'number') params.limit = String(limit);
    const { data } = await apiClient.get<SentenceDrillResponse>(
      '/review/custom/sentence-drill',
      { params },
    );
    return data;
  },
};

export default reviewCenterService;
